import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useNotifications } from './useNotifications';
import { useAuthContext } from './useAuthContext';

/**
 * Maps route paths to page types for notification tracking
 * @param {string} pathname - The current route pathname
 * @returns {string|null} - The page type or null if not tracked
 */
const getPageTypeFromPath = (pathname) => {
  if (pathname.startsWith('/forum')) {
    return 'forum';
  }
  if (pathname === '/jobs' || pathname.startsWith('/jobs/')) {
    return 'opportunities';
  }
  if (pathname === '/network' || pathname.startsWith('/network/')) {
    return 'people';
  }
  return null;
};

/**
 * Custom hook that automatically tracks page visits for notification clearing
 * This hook monitors route changes and marks pages as visited when users navigate to them
 * 
 * For content pages (forum, opportunities, people), we delay marking as visited to allow 
 * users to see "new" badges on items that were created since their last visit
 */
export const usePageVisitTracking = () => {
  const location = useLocation();
  const { user, userProfile } = useAuthContext();
  const { markPageVisited, clearNotificationCount } = useNotifications();

  useEffect(() => {
    // Don't track page visits for waitlist users or if user profile is not loaded
    if (!user || (userProfile && userProfile.status === 'waitlist') || !userProfile) {
      return;
    }
    
    const pageType = getPageTypeFromPath(location.pathname);
    
    if (pageType) {
      // Immediately clear notification count for instant user feedback
      clearNotificationCount(pageType);
      
      // Reduce delay to minimize navigation impact while preserving "new" badge behavior
      const delay = ['forum', 'opportunities', 'people'].includes(pageType) ? 500 : 0; // 500ms for content pages, immediate for others
      
      const timer = setTimeout(() => {
        // Mark the page as visited, which will update the last visit time in the database
        // Use requestIdleCallback if available to avoid blocking navigation
        if (window.requestIdleCallback) {
          window.requestIdleCallback(() => {
            markPageVisited(pageType).catch(error => {
              console.warn(`Failed to mark ${pageType} as visited:`, error);
            });
          });
        } else {
          markPageVisited(pageType).catch(error => {
            console.warn(`Failed to mark ${pageType} as visited:`, error);
          });
        }
      }, delay);
      
      // Cleanup timer if component unmounts or dependencies change
      return () => clearTimeout(timer);
    }
  }, [location.pathname, user, userProfile, markPageVisited, clearNotificationCount]);
};

export { getPageTypeFromPath };